import React, { useEffect, useState, Suspense, useRef } from 'react';
import Lenis from 'lenis';
import 'lenis/dist/lenis.css';
import { Routes, Route, useNavigate, Navigate, useLocation } from 'react-router-dom';
import { auth } from "./firebase";
import { signOut, onAuthStateChanged } from "firebase/auth";
import { saveUserToFirestore } from './dbFunctions';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import ErrorBoundary from './components/ErrorBoundary';
import AuthModal from './components/AuthModal';
import CookieConsent from './components/CookieConsent';
const Home = React.lazy(() => import('./pages/Home'));
const Profile = React.lazy(() => import('./pages/Profile'));
const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const Settings = React.lazy(() => import('./pages/Settings'));
const PublicProfile = React.lazy(() => import('./pages/PublicProfile'));
const PublicDashboard = React.lazy(() => import('./pages/PublicDashboard'));
const Community = React.lazy(() => import('./pages/Community'));
const CommunityTemplateView = React.lazy(() => import('./pages/CommunityTemplateView'));
const AdminPanel = React.lazy(() => import('./pages/AdminPanel'));
const WidgetShowcase = React.lazy(() => import('./pages/WidgetShowcase'));
const Themes = React.lazy(() => import('./pages/Themes'));
const LearningPath = React.lazy(() => import('./pages/LearningPath'));
const Legal = React.lazy(() => import('./pages/Legal'));
const MobileHub = React.lazy(() => import('./pages/MobileHub'));
const MobileHome = React.lazy(() => import('./pages/MobileHome'));
const MobileExplore = React.lazy(() => import('./pages/MobileExplore'));
import MobileNavBar from './components/MobileNavBar';
import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import { useTheme } from './context/ThemeContext';
import './App.css';

const THEME_COLORS = {
  dark: '#14181C',
  light: '#FAF9F6',
  landing: '#FAFAF9',
  crimson_empire: '#343131',
  teal_garden: '#F5F5F0',
  rose_clay: '#FEF6F2',
  sage_slate: '#F4F9F4',
  sunset_fire: '#FEF9F0',
  neon_dream: '#331D2C',
  ember_night: '#2D3250',
  ocean_breeze: '#F8F9FA',
  berry_blast: '#FFF5FA',
  cosmic_night: '#151515',
};

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authModalMode, setAuthModalMode] = useState('signin');
  const navigate = useNavigate();
  const location = useLocation();
  const { colorMode, isDark } = useTheme();
  const isDashboard = location.pathname.startsWith('/dagenda/') ||
    location.pathname.startsWith('/template/') ||
    location.pathname.startsWith('/preview/');
  const isNativeApp = Capacitor.isNativePlatform();
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsSmallScreen(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Native Status Bar Management
  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      const updateStatusBar = async () => {
        try {
          const color = THEME_COLORS[colorMode] || THEME_COLORS.dark;
          await StatusBar.setBackgroundColor({ color });
          await StatusBar.setStyle({ style: isDark ? Style.Dark : Style.Light });
        } catch (e) {
          console.warn('StatusBar error:', e);
        }
      };
      updateStatusBar();
    }
  }, [colorMode, isDark]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser && !currentUser.emailVerified && currentUser.providerData[0]?.providerId === 'password') {
        setUser(null);
        setLoading(false);
        return;
      }
      setUser(currentUser);
      setLoading(false);
      if (currentUser) {
        await saveUserToFirestore(currentUser);
      }
    });
    return () => unsubscribe();
  }, [navigate]);
  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      orientation: 'vertical',
      gestureOrientation: 'vertical',
      smoothWheel: true,
      wheelMultiplier: 1,
      smoothTouch: false,
      touchMultiplier: 2,
      infinite: false,
    });

    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }

    requestAnimationFrame(raf);

    // Make lenis accessible globally for programmatic scrolling
    window.lenis = lenis;

    return () => {
      lenis.destroy();
    };
  }, []);

  useEffect(() => {
    if (window.lenis) {
      window.lenis.scrollTo(0, { immediate: true });
    } else {
      window.scrollTo(0, 0);
    }
  }, [location.pathname]);
  const openSignIn = () => {
    setAuthModalMode('signin');
    setAuthModalOpen(true);
  };
  const openRegister = () => {
    setAuthModalMode('register');
    setAuthModalOpen(true);
  };
  const closeAuthModal = () => {
    setAuthModalOpen(false);
  };
  const handleLogout = () => {
    signOut(auth);
    navigate("/");
  };
  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', backgroundColor: '#14181C', color: 'white', fontSize: '24px' }}>
        Dagenda Yükleniyor... (Firebase Bağlantısı Bekleniyor) ⏳
      </div>
    );
  }
  const LoadingScreen = () => (
    <div className="app-loading-screen">
      Dagenda Yükleniyor... ⏳
    </div>
  );
  return (
    <ErrorBoundary>
      <div className={`app-container ${isNativeApp ? 'is-mobile-app' : (isSmallScreen ? 'is-mobile-view' : '')}`}>
        {(!isDashboard && !(isNativeApp && user)) && (
          <Navbar
            user={user}
            onLogin={openSignIn}
            onRegister={openRegister}
            onLogout={handleLogout}
          />
        )}
        {isNativeApp && user && !isDashboard && (
          <MobileNavBar user={user} />
        )}
        { }
        <AuthModal
          isOpen={authModalOpen}
          onClose={closeAuthModal}
          initialMode={authModalMode}
        />
        <CookieConsent />
        <Suspense fallback={<LoadingScreen />}>
          <Routes>
            <Route path="/" element={
              user ? (
                isNativeApp ? <Navigate to="/hub" replace /> : <Home user={user} onLogin={openSignIn} onRegister={openRegister} />
              ) : (
                <Home
                  user={user}
                  onLogin={openSignIn}
                  onRegister={openRegister}
                />
              )
            } />
            <Route path="/profile" element={
              user ? (
                isNativeApp ? <Navigate to="/hub" /> : <Profile user={user} />
              ) : <Navigate to="/" />
            } />
            <Route path="/hub" element={
              user ? <MobileHome user={user} /> : <Navigate to="/" />
            } />
            <Route path="/agendas" element={
              user ? <MobileHub user={user} /> : <Navigate to="/" />
            } />
            <Route path="/explore" element={
              user ? <MobileExplore user={user} /> : <Navigate to="/" />
            } />
            <Route path="/dagenda/:id" element={
              user ? <Dashboard user={user} /> : <Navigate to="/" />
            } />
            <Route path="/preview/:id" element={
              <PublicDashboard />
            } />
            <Route path="/template/:id" element={
              <CommunityTemplateView />
            } />
            <Route path="/settings/*" element={
              user ? <Settings user={user} /> : <Navigate to="/" />
            } />
            { }
            <Route path="/user/:username" element={
              <PublicProfile currentUser={user} />
            } />
            { }
            <Route path="/community" element={
              <Community user={user} />
            } />
            { }
            <Route path="/widgets" element={
              <WidgetShowcase />
            } />
            { }
            <Route path="/themes" element={
              <Themes />
            } />
            { }
            <Route path="/rehber" element={
              <LearningPath />
            } />
            { }
            <Route path="/legal" element={
              <Legal />
            } />
            <Route path="/kullanim-kosullari" element={<Navigate to="/legal?tab=terms" replace />} />
            <Route path="/gizlilik-politikasi" element={<Navigate to="/legal?tab=privacy" replace />} />
            <Route path="/kvkk" element={<Navigate to="/legal?tab=privacy" replace />} />
            { }
            <Route path="/admin" element={
              user ? <AdminPanel user={user} /> : <Navigate to="/" />
            } />
            <Route path="/dashboard" element={<Navigate to="/profile" />} />
          </Routes>
        </Suspense>
        {location.pathname === '/' && <Footer />}
      </div>
    </ErrorBoundary>
  );
}
export default App;
